import { isEmpty } from "lodash"

import { ACTIVITY_SESSION_TYPE, SECTION_TYPE } from "@/app/report/common/constants"
import { SectionSummary, SummaryData } from "@/app/report/common/constructor"
import { getSummaryImplValue, parseSummary } from "@/app/report/common/tools"
import type {
  MetricMapType,
  ReportChartActivity,
  ReportChartSession,
  SummarySettings,
} from "@/app/report/common/type"
import { SquareUnit } from "@/constants/unit"
import type { PlayerSessionSummary, ReportTemplate } from "@/types/report"
import type { Unit } from "@/types/setting"

/**
 * Summary 섹션 — 리포트 화면(daily/page.tsx)과 PDF 빌더(BuilderSummary)가 함께 쓰는 단일 구현 (일감 #75).
 * 두 화면의 항목·값이 어긋나지 않도록 섹션을 만드는 규칙은 이 파일 밖에 두지 않는다.
 */

/** 현재 모드에 해당하는 선수 요약만 고른다 — 반대편 모드의 잔여 데이터는 절대 쓰지 않는다 (#177) */
export const selectPlayerSummaryByMode = (
  activitySessionType: ACTIVITY_SESSION_TYPE,
  activity?: ReportChartActivity,
  session?: ReportChartSession,
): PlayerSessionSummary | null =>
  activitySessionType === ACTIVITY_SESSION_TYPE.SESSION
    ? (session?.playerSessionSummary ?? null)
    : (activity?.playerActivitySummary ?? null)

/**
 * 리포트 템플릿에서 Summary 섹션 설정을 꺼낸다. 일별 템플릿은 Summary 섹션을 하나만 두므로 첫 항목을 쓴다.
 * 섹션이 없거나 설정 형태가 배열이 아니면 null — 호출한 쪽이 "템플릿에 Summary 없음" 으로 처리한다.
 */
export const getTemplateSummarySettings = (
  template?: ReportTemplate | null,
): SummarySettings[] | null => {
  const section = template?.content?.find((item) => item?.section === SECTION_TYPE.SUMMARY)
  if (!section || !Array.isArray(section.settings)) {
    return null
  }

  return section.settings as SummarySettings[]
}

/** 템플릿 Summary 설정 + 선수 요약(total/average) → 화면에 그릴 SectionSummary */
export const buildSummarySection = ({
  summarySettings,
  metricMap,
  selectedUnitList,
  playerSummary,
}: {
  summarySettings: SummarySettings[] | null | undefined
  metricMap: MetricMapType
  selectedUnitList: Unit[]
  playerSummary: PlayerSessionSummary | null | undefined
}): SectionSummary | null => {
  if (
    !summarySettings ||
    isEmpty(summarySettings) ||
    isEmpty(metricMap) ||
    isEmpty(selectedUnitList)
  ) {
    return null
  }

  const { total, average } = playerSummary || {}
  const settings = structuredClone(summarySettings)

  const parsedTotal = parseSummary((total ?? {}) as Record<string, unknown>)
  const parsedAverage = parseSummary((average ?? {}) as Record<string, unknown>)

  const summaries = settings.map((settingsItem) => {
    const source = settingsItem.calcType === "total" ? parsedTotal : parsedAverage

    const { value, unit: rawUnit } = getSummaryImplValue(
      settingsItem.metric,
      source,
      metricMap,
      selectedUnitList,
    )
    const unit = rawUnit?.includes("^2") ? SquareUnit[rawUnit] : rawUnit

    return new SummaryData({
      title: settingsItem.title,
      metric: settingsItem.metric,
      calcType: settingsItem.calcType,
      data: value,
      unit,
    })
  })

  return new SectionSummary(summaries)
}
